import moment from 'moment-timezone';
import { format as timeAgoFormat } from 'timeago.js';

import { reportErrorInProductionOrThrow } from './Utils';

type TimeUnit = 'seconds' | 'minutes' | 'hours' | 'days' | 'months' | 'years';

interface DateComparisonResult {
  readonly BEFORE: -1;
  readonly EQUAL: 0;
  readonly AFTER: 1;
}

type DateInput = string | Date;

export const DATE_COMPARISON: DateComparisonResult = {
  BEFORE: -1,
  EQUAL: 0,
  AFTER: 1,
} as const;

const DEFAULT_MOCK_DATE = '2018-01-01';
const MINIMUM_AGE = 18;

export enum DateFormat {
  ISO = 'YYYY-MM-DD',
  US = 'MM/DD/YYYY',
  TIME_24H = 'HH:mm',
  TIME_24H_WITH_SECONDS = 'HH:mm:ss',
  FULL_DATE_TIME = 'MMMM Do YYYY, h:mm A',
  LOCAL_DATE_TIME = 'L LT',
  LOCAL_DATE = 'L',
  LOCAL_TIME = 'LT',
  LOCAL_TIME_WITH_SECONDS = 'LTS',
  WEEKDAY_SHORT_MONTH_DAY = 'dddd, MMM D',
  UTC_ISO = 'YYYY-MM-DDTHH:mm:ss.SSS[Z]',
  SHORT_MONTH_DAY = 'MMM D',
}

// most of the function below will return the current DateTime when given 'undefined', hiding many bugs
function verifyParam(param: unknown, paramName = 'parameter'): void {
  if (!param) {
    throw new Error(`Empty ${paramName} given`);
  }
}

export function getLocalDateToday(): string {
  return moment().format(DateFormat.ISO);
}

export function getAmericanDateFormat(date: DateInput): string {
  // verifyParam(date, 'date');
  return moment(date).format(DateFormat.US);
}

export function getDateFromServerDateTime(date: DateInput): string {
  verifyParam(date, 'date');
  return moment(date).format(DateFormat.LOCAL_DATE);
}

export function getLocalDateTime(date: DateInput): string {
  verifyParam(date, 'date');
  return moment(date).format(DateFormat.ISO);
}

export function isoDateToUs(isoDate: DateInput): string {
  verifyParam(isoDate, 'isoDate');
  return moment.utc(isoDate).format(DateFormat.LOCAL_DATE);
}

export function isoUtcDateTimeToLocal(isoDate: DateInput): string {
  verifyParam(isoDate, 'isoDate');
  return moment.utc(isoDate).local().format(DateFormat.LOCAL_DATE_TIME);
}

export function isoDateTimeToLocal(isoDate: DateInput): string {
  verifyParam(isoDate, 'isoDate');
  return moment(isoDate).local().format(DateFormat.LOCAL_DATE_TIME);
}

export function timeToLocalTime(time: string): string {
  verifyParam(time, 'time');
  // pretending a fictive date before the time for moment to work
  return moment.utc(`${DEFAULT_MOCK_DATE} ${time}`).format(DateFormat.LOCAL_TIME);
}

export function serverDateToLocal(serverDate: DateInput): string {
  verifyParam(serverDate, 'serverDate');
  if (typeof serverDate === 'string' && isDateOnlyFormat(serverDate)) {
    reportErrorInProductionOrThrow(
      `ServerDateToLocal was called with a date-only string: "${serverDate}". Avoid timezone manipulation when using date-only strings, as it may lead to inaccurate results.`,
      true
    );
    return serverDateFormatWithoutTZChange(serverDate);
  }
  return moment.utc(serverDate).local().format(DateFormat.LOCAL_DATE);
}

export function serverDateFormatWithoutTZChange(serverDate: DateInput): string {
  verifyParam(serverDate, 'serverDate');
  return moment.utc(serverDate).format(DateFormat.LOCAL_DATE);
}

export function serverDateTimeToLocal(serverDateTime: DateInput): string {
  verifyParam(serverDateTime, 'serverDateTime');
  return moment.utc(serverDateTime).local().format(DateFormat.LOCAL_DATE_TIME);
}

export function serverDateTimeToLocalMoment(serverDateTime: DateInput): moment.Moment {
  verifyParam(serverDateTime, 'serverDateTime');
  return moment.utc(serverDateTime).local();
}

export function serverDateToLocalMoment(serverDate: DateInput, showTimeZone = true): string {
  verifyParam(serverDate, 'serverDate');
  if (moment.locale() === 'en') {
    return moment.utc(serverDate).tz('America/New_York').format(DateFormat.LOCAL_DATE) + (showTimeZone ? ' EST' : '');
  }

  return moment.utc(serverDate).local().format(DateFormat.LOCAL_DATE);
}

export function serverDateTimeToLocalDate(serverDateTime: DateInput): string {
  verifyParam(serverDateTime, 'serverDateTime');
  return moment.utc(serverDateTime).local().format(DateFormat.ISO);
}

export function localTimeToViewTime(localTime: string): string {
  verifyParam(localTime, 'localTime');
  return moment.utc(`${DEFAULT_MOCK_DATE} ${localTime}`).format(DateFormat.LOCAL_TIME);
}

export function compareDatesStrings(
  date1: string | Date,
  date2: string | Date
): DateComparisonResult[keyof DateComparisonResult] {
  // verifyParam(date1, 'date1');
  // verifyParam(date2, 'date2');

  if (moment(date1).isBefore(moment(date2))) return DATE_COMPARISON.BEFORE;
  if (moment(date1).isAfter(moment(date2))) return DATE_COMPARISON.AFTER;
  return DATE_COMPARISON.EQUAL;
}

export const compareDatesForSort = (date1: DateInput, date2: DateInput): number =>
  new Date(date1 as string | Date).getTime() - new Date(date2 as string | Date).getTime();

export function areSameLocalDate(localDate: string | Date, serverUtcDate: string | Date): boolean {
  if (!localDate || !serverUtcDate) {
    return false;
  }
  return moment(localDate).isSame(moment.utc(serverUtcDate), 'day');
}

export function datesYearsDiff(date1: DateInput, date2: DateInput): number | undefined {
  if (!date1 || !date2) {
    return undefined;
  }
  return moment(date1).diff(date2, 'years', true);
}

export function isDatesDiffLessThan18Years(date1?: DateInput, date2?: DateInput): boolean {
  if (!date1 || !date2) {
    return false;
  }
  return Math.abs(datesYearsDiff(date1, date2) ?? 0) < MINIMUM_AGE;
}

export const datesDiffFromUtcNow = (dateUtc: string, unitOfTime: TimeUnit = 'seconds'): number | undefined => {
  if (!dateUtc) {
    return undefined;
  }

  return moment.utc().diff(moment.utc(dateUtc), unitOfTime, true);
};

export const convertSecondsToHours = (seconds: number): number => {
  if (seconds < 0) {
    throw new Error('Seconds must be a positive number');
  }
  return Math.round(seconds / 3600);
};

export const stringDateIsOfFormat = (date: string, format: DateFormat): boolean => moment(date, format, true).isValid();

export const isISOFormat = (date?: string): boolean => {
  return !!date?.includes('T');
};

export const isDateOnlyFormat = (date?: string): boolean => {
  return !!date?.match(/^\d{4}-\d{2}-\d{2}$/);
};

export const getDateTimeFromUtcByUnit = ({
  dateUtc,
  unitOfTime = 'months',
  additionInUnit = 0,
  format = DateFormat.UTC_ISO,
}: {
  dateUtc: string;
  unitOfTime?: TimeUnit;
  additionInUnit?: number;
  format?: DateFormat;
}): string | undefined => {
  if (!dateUtc) {
    return undefined;
  }

  return moment.utc(dateUtc).add(additionInUnit, unitOfTime).startOf('day').format(format);
};

export function localToServerDateTimeNoonUTC(localDate: DateInput | moment.Moment): string {
  verifyParam(localDate, 'localDate');

  // If input is a Moment object, use it directly
  if (moment.isMoment(localDate)) {
    return localDate.hour(12).minute(0).second(0).millisecond(0).format(DateFormat.UTC_ISO);
  }

  // Check if the input is just a date string (e.g., '2024-08-26') without time info
  if (typeof localDate === 'string' && isDateOnlyFormat(localDate)) {
    // Create a moment object in the current timezone and set it to start of day
    const localMoment = moment(localDate).startOf('day');
    // Convert to UTC
    return localMoment.hour(12).minute(0).second(0).millisecond(0).format(DateFormat.UTC_ISO);
  }

  // For full datetime strings or Date objects, convert to UTC
  return moment(localDate).hour(12).minute(0).second(0).millisecond(0).format(DateFormat.UTC_ISO);
}

export function localDateToEndOfDayUTC(localDate: DateInput): string {
  verifyParam(localDate, 'localDate');
  return moment(localDate).endOf('day').utc().format(DateFormat.UTC_ISO);
}

export function isDateBeforeToday(serverUtcDate: DateInput): boolean {
  if (!serverUtcDate) {
    return false;
  }
  return moment.utc(serverUtcDate).local().isBefore(moment(), 'day');
}

export const getRelativeTimeText = (date?: DateInput): string => {
  if (!date) {
    return '';
  }

  return timeAgoFormat(date);
};

/**
 * Formats a local date for short display:
 * - If the date is from today: displays the time (e.g., "2:30 PM")
 * - If the date is before today: displays the short date (e.g., "Dec 30")
 *
 * Note: This function expects a local date. Use serverDateTimeToLocalDate() to convert UTC dates first.
 */
export const formatShortDate = (localDate?: DateInput): string => {
  if (!localDate) {
    return '';
  }

  const date = moment(localDate);
  const today = moment().startOf('day');

  if (date.isSame(today, 'day')) {
    return date.format(DateFormat.LOCAL_TIME);
  }

  return date.format(DateFormat.SHORT_MONTH_DAY);
};

/**
 * Local time down to the second, in the organization's locale.
 *
 * For streams whose entries land within the same minute, where HH:MM alone is ambiguous. Locale
 * rather than `TIME_24H_WITH_SECONDS`, so an organization on a 12h locale keeps reading 12h.
 *
 * Returns '' for an unparseable timestamp instead of throwing: these render per row beside live
 * data, and one bad row must not take the list with it.
 */
export const formatLocalTimeWithSeconds = (dateTime?: DateInput): string => {
  if (!dateTime) {
    return '';
  }

  const parsed = moment(dateTime);

  return parsed.isValid() ? parsed.local().format(DateFormat.LOCAL_TIME_WITH_SECONDS) : '';
};

/**
 * Local day as a feed separator reads it ("Tuesday, Jun 2"), in the organization's locale.
 *
 * Same '' fallback, and for the same reason, as `formatLocalTimeWithSeconds`.
 */
export const formatLocalDayLabel = (dateTime?: DateInput): string => {
  if (!dateTime) {
    return '';
  }

  const parsed = moment(dateTime);

  return parsed.isValid() ? parsed.local().format(DateFormat.WEEKDAY_SHORT_MONTH_DAY) : '';
};

/**
 * Validates if a date string is complete and valid for API submission
 * Returns true only for properly formatted, complete dates
 * Supports common user input formats: MM/DD/YYYY, DD/MM/YYYY, YYYY-MM-DD, etc.
 *
 * This function is more strict than basic date parsing - it ensures the date string
 * is complete and has reasonable formatting before sending to APIs.
 */
export function isValidCompleDateString(dateString: string | null | undefined): boolean {
  if (!dateString || dateString.trim() === '') {
    return false;
  }

  const trimmed = dateString.trim();

  // Reject obviously invalid strings like "Invalid date"
  if (trimmed === 'Invalid date' || trimmed.toLowerCase().includes('invalid')) {
    return false;
  }

  try {
    // Check if the date string can be parsed into a valid Date object
    const date = new Date(trimmed);

    // Check if the date is valid (not NaN)
    const isValidDate = !isNaN(date.getTime());

    if (!isValidDate) {
      return false;
    }

    // Additional check: ensure the date string has reasonable length (not just partial input)
    // This prevents sending incomplete dates like "2024-" or "12/" or "1"
    // Allow for various formats:
    // - "1/1/2024" (8 chars) - US short format
    // - "01/01/2024" (10 chars) - US format
    // - "2024-01-01" (10 chars) - ISO format
    // - "20/01/2024" (10 chars) - EU format
    const hasReasonableLength = trimmed.length >= 6; // Minimum like "1/1/24"

    // Additional validation: check if it contains date separators
    const hasDateSeparators = /[/\-.]/.test(trimmed);

    return hasReasonableLength && hasDateSeparators;
  } catch {
    return false;
  }
}

export const getDaysDiff = (startDate: DateInput, endDate: DateInput, precise = false): number => {
  return moment(endDate).diff(startDate, 'days', precise);
};

export const getPeriodInDays = (startDate: DateInput, endDateInclusive: DateInput, precise = false): number => {
  return getDaysDiff(startDate, endDateInclusive, precise) + 1;
};

// The date/time pickers build their input mask from fixed-length tokens, so a lone hour/day/month
// token in a locale's format (`h:mm A`, `H:mm`, `D.MM.YYYY`, `MM/D/YYYY`) makes MUI's mask probe fail
// and fall back to an unmasked input. Pad each lone `h`/`H`/`D`/`M` to its doubled form (`h`→`hh`,
// `D`→`DD`, …), skipping bracketed `[...]` literals (moment's escape, so `[Uhr]` is not mangled) and
// leaving already-padded runs, minutes (`mm`), the year, month names (`MMM`) and separators untouched.
// This covers token length only. Locales that render digits as non-ASCII numerals (ar, hi, bn, fa, …)
// still fall back to an unmasked input, because MUI's mask probe matches digits with ASCII `\d` and
// their glyphs don't match — a separate MUI limitation.
export function maskableFormat(format: string): string {
  // Alternate per token (`h+|H+|D+|M+`) rather than `[hHDM]+`, so an adjacent mixed run like `DM` is
  // matched as two length-1 tokens and each is padded. A bracket match is always ≥2 chars, so the
  // `length === 1` check leaves `[...]` literals untouched without a separate guard.
  return format.replace(/\[[^\]]*\]|h+|H+|D+|M+/g, (token) => (token.length === 1 ? token + token : token));
}
